import * as Sentry from '@sentry/react';
import { SentryTeamNames } from '@frontend/sentry-teams';

type WeaveFeatures =
  | 'payments'
  | 'contacts'
  | 'reviews'
  | 'phone'
  | 'messages'
  | 'analytics'
  | 'email'
  | 'lists'
  | 'fax'
  | 'team chat'
  | 'scheduling'
  | 'onboarding'
  | 'auth'
  | 'office-assistant'
  | 'infra'
  | 'user-preferences'
  | 'notifications'
  | 'forms-submissions'
  | 'customer-lifecycle-management';

type StringWithAutocomplete<T> = T | (string & Record<never, never>);

type SentryTopic = StringWithAutocomplete<WeaveFeatures>;

type LogSeverityLevel = 'log' | 'info';

type LogType =
  /* Describes a generic breadcrumb. This is typically a log message
or user-generated breadcrumb. */
  | 'default'
  /* This is typically a log message. */
  | 'debug'
  /* An error that occurred before the exception. */
  | 'error'
  /* A navigation event can be a URL change in a web application,
or a UI transition in a mobile or desktop application, etc. */
  | 'navigation'
  /* This represents an HTTP request transmitted from your application. */
  | 'http'
  /* Information that helps identify the root cause of the
issue or for whom the error occurred. */
  | 'info'
  /* This represents a query that was made in your application. */
  | 'query'
  /*  Describes a tracing event. */
  | 'transaction'
  /* A user interaction with your app's UI. */
  | 'ui';

type SentryContext = {
  name: string;
  context: Record<string, any>;
};

export interface SentryLog {
  message: string;
  data?: Record<string, unknown>;
  severityLevel?: LogSeverityLevel;
  type?: LogType;
  team: SentryTeamNames;
  category?: SentryTopic | 'log';
}

/**
 * NOTE this doesn't explicity send a message to Sentry, it just adds a breadcrumb, which is like an interesting event that might get sent in an error trace.
 * See https://docs.sentry.io/platforms/javascript/enriching-events/breadcrumbs/
 */
export const addBreadcrumb = ({ message, data, severityLevel = 'info', category = 'log' }: Omit<SentryLog, 'team'>) => {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level: severityLevel,
  });
};

export const log = ({ message, team, data, severityLevel = 'log', category = 'log' }: SentryLog) => {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level: severityLevel,
  });
  Sentry.captureMessage(message, {
    level: severityLevel,
    extra: data,
    tags: {
      team,
    },
  });
};
export interface SentryWarning {
  error: any;
  topic: SentryTopic;
  team: SentryTeamNames;
  addContext?: SentryContext;
}

export const warn = ({ error, topic, team, addContext }: SentryWarning) => {
  Sentry.withScope((scope) => {
    scope.setTag('topic', topic);
    scope.setTag('team', team);
    scope.setLevel('warning');
    if (!!addContext) scope.setContext(addContext.name, addContext.context);
    Sentry.captureMessage(error);
  });
};

type ErrorSeverityLevel = 'error' | 'fatal';

export interface SentryError {
  error: any;
  topic: SentryTopic;
  team: SentryTeamNames;
  severityLevel?: Extract<Sentry.SeverityLevel, ErrorSeverityLevel>;
  addContext?: SentryContext;
  sampleRate?: number; // between 0 and 1. This overrides the global sample rate and only works in gatekeeper
}

export const error = ({ error, team, topic, severityLevel = 'error', addContext, sampleRate }: SentryError) => {
  Sentry.withScope((scope) => {
    scope.setTag('topic', topic);
    scope.setTag('team', team);
    scope.setLevel(severityLevel);
    scope.setContext('meta', { sampleRate });
    if (!!addContext) scope.setContext(addContext.name, addContext.context);
    Sentry.captureException(error);
  });
};
